import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Search, Loader2, BadgeCheck, Flag, SearchX, MessageSquareReply, Send, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResultCard = ({ r, i }) => {
  const [respondOpen, setRespondOpen] = useState(false);
  const [resp, setResp] = useState({ responder_name: "", responder_email: "", response_text: "" });
  const [respStatus, setRespStatus] = useState({ state: "idle", msg: "" });
  const [resendState, setResendState] = useState("idle");

  const resendConfirmation = async () => {
    setResendState("loading");
    try {
      await axios.post(`${API}/reviews/${r.id}/respond/resend`, { responder_email: resp.responder_email });
      setResendState("done");
    } catch {
      setResendState("idle");
    }
  };

  const submitResponse = async (e) => {
    e.preventDefault();
    setRespStatus({ state: "loading", msg: "" });
    try {
      const { data } = await axios.post(`${API}/reviews/${r.id}/respond`, resp);
      setRespStatus({ state: "success", msg: data.message });
    } catch (err) {
      const d = err.response?.data?.detail;
      setRespStatus({ state: "error", msg: typeof d === "string" ? d : "Could not submit — try again later." });
    }
  };

  return (
    <motion.article
      data-testid={`search-result-${i}`}
      className="rounded-2xl bg-white border border-black/5 shadow-[0_10px_36px_-14px_rgba(17,24,39,0.12)] p-6 sm:p-7"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: i * 0.07 }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display font-semibold text-lg tracking-tight">{r.product_name}</h3>
          <p className="mt-0.5 text-sm text-gray-500">{r.seller_name}</p>
          {r.reviewer && (
            <Link
              data-testid={`search-reviewer-link-${i}`}
              to={`/reviewer/${r.reviewer.id}`}
              className="mt-1 inline-block text-xs font-semibold text-trust hover:text-trust-dark transition-colors duration-200"
            >
              Filed by {r.reviewer.name}
            </Link>
          )}
        </div>
        <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-verified-soft text-verified text-[11px] font-bold px-3 py-1.5 uppercase tracking-wider">
          <BadgeCheck size={13} /> Verified
        </span>
      </div>
      <div className="receipt-dash my-4" />
      <dl className="space-y-2.5 text-sm">
        <div className="flex justify-between gap-4"><dt className="text-gray-500">Paid</dt><dd className="font-semibold">{r.price_paid}</dd></div>
        <div className="flex justify-between gap-4"><dt className="text-gray-500 shrink-0">Promised</dt><dd className="font-semibold text-right">{r.promised}</dd></div>
        <div className="flex justify-between gap-4"><dt className="text-gray-500 shrink-0">Received</dt><dd className="font-semibold text-right text-warn">{r.received}</dd></div>
        <div className="flex justify-between gap-4"><dt className="text-gray-500">Upsell pushed</dt><dd className={`font-semibold ${r.upsell_pushed === "yes" ? "text-warn" : ""}`}>{r.upsell_pushed === "yes" ? "Yes" : "No"}</dd></div>
        <div className="flex justify-between gap-4"><dt className="text-gray-500">Refund offered</dt><dd className={`font-semibold ${r.refund_offered === "no" ? "text-warn" : "text-verified"}`}>{r.refund_offered === "yes" ? "Yes" : "No"}</dd></div>
      </dl>
      <div className="receipt-dash my-4" />
      <div className="flex items-center justify-between gap-3">
        <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-gray-400">Proof on file</span>
        {r.resell_rights === "yes" && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gold-soft text-yellow-700 text-[11px] font-bold px-3 py-1.5 uppercase tracking-wider">
            <Flag size={12} /> Resell-rights pack
          </span>
        )}
      </div>

      {r.seller_response && (
        <div data-testid={`seller-response-${i}`} className="mt-4 rounded-xl border-l-4 border-trust bg-trust-soft/60 p-4">
          <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-trust">
            <MessageSquareReply size={13} /> Response from the seller{r.seller_response.responder_name ? ` — ${r.seller_response.responder_name}` : ""}
          </p>
          <p className="mt-2 text-sm text-gray-700">{r.seller_response.text}</p>
        </div>
      )}

      {!r.seller_response && !respondOpen && respStatus.state !== "success" && (
        <button
          data-testid={`seller-respond-open-${i}`}
          onClick={() => setRespondOpen(true)}
          className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-trust transition-colors duration-200"
        >
          <MessageSquareReply size={13} /> Are you the seller? Respond
        </button>
      )}

      {respondOpen && respStatus.state !== "success" && (
        <form onSubmit={submitResponse} className="mt-4 rounded-xl bg-paper border border-black/5 p-4 space-y-3">
          <p className="text-xs text-gray-500 leading-relaxed">
            Same rules as reviews: describe facts, no abuse back at the buyer. Your response appears beside this
            receipt, clearly labeled, once it's moderated.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid={`respond-name-${i}`} required minLength={2} value={resp.responder_name}
              onChange={(e) => setResp((s) => ({ ...s, responder_name: e.target.value }))}
              placeholder="Your name / brand" className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-trust" />
            <input data-testid={`respond-email-${i}`} required type="email" value={resp.responder_email}
              onChange={(e) => setResp((s) => ({ ...s, responder_email: e.target.value }))}
              placeholder="Your email (not published)" className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-trust" />
          </div>
          <textarea data-testid={`respond-text-${i}`} required minLength={10} maxLength={2000} rows={3} value={resp.response_text}
            onChange={(e) => setResp((s) => ({ ...s, response_text: e.target.value }))}
            placeholder="State your facts: what was delivered, refund policy, what you'll fix…"
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-trust resize-none" />
          <div className="flex items-center gap-3">
            <button data-testid={`respond-submit-${i}`} type="submit" disabled={respStatus.state === "loading"}
              className="inline-flex items-center gap-1.5 rounded-full bg-trust text-white px-5 py-2.5 text-xs font-semibold transition-colors duration-200 hover:bg-trust-dark disabled:opacity-60">
              {respStatus.state === "loading" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Submit response
            </button>
            <button type="button" onClick={() => setRespondOpen(false)} className="text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors duration-200">
              Cancel
            </button>
          </div>
          {respStatus.state === "error" && <p className="text-xs font-semibold text-warn">{respStatus.msg}</p>}
        </form>
      )}

      {respStatus.state === "success" && (
        <div className="mt-4">
          <p data-testid={`respond-success-${i}`} className="flex items-center gap-2 text-xs font-semibold text-verified">
            <CheckCircle2 size={14} /> {respStatus.msg}
          </p>
          <button
            data-testid={`respond-resend-${i}`}
            onClick={resendConfirmation}
            disabled={resendState !== "idle"}
            className="mt-2 text-xs font-semibold text-gray-400 hover:text-trust transition-colors duration-200 underline underline-offset-2 disabled:opacity-60"
          >
            {resendState === "done" ? "Fresh link sent — check spam too" : resendState === "loading" ? "Sending…" : "Didn't get the email? Resend confirmation"}
          </button>
        </div>
      )}
    </motion.article>
  );
};

export const SearchSection = () => {
  const [q, setQ] = useState("");
  const [state, setState] = useState({ phase: "idle", results: [], searched: "" });

  const search = async (e) => {
    e.preventDefault();
    if (q.trim().length < 2) return;
    setState({ phase: "loading", results: [], searched: q.trim() });
    try {
      const { data } = await axios.get(`${API}/reviews/search`, { params: { q: q.trim() } });
      setState({ phase: "done", results: data.results, searched: q.trim() });
    } catch {
      setState({ phase: "done", results: [], searched: q.trim() });
    }
  };

  return (
    <section data-testid="search-section" className="py-24 sm:py-32">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-4">
            <span className="font-display text-4xl sm:text-5xl font-bold outline-word leading-none select-none" aria-hidden="true">04</span>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-trust">Try it live</p>
          </div>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
            Search before you pay.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-gray-600 max-w-2xl">
            Type a seller, course, or bundle name. You'll only ever see verified receipts here — pending and
            unproven reviews never show up.
          </p>

          <form onSubmit={search} className="mt-8 flex flex-col sm:flex-row gap-3">
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder='Try a seller or product name — e.g. "108 BOQ"'
              aria-label="Search sellers and products"
              className="flex-1 rounded-full border border-gray-200 bg-white px-6 py-4 text-base outline-none transition-colors duration-200 focus:border-trust placeholder:text-gray-400"
            />
            <button
              data-testid="search-submit-button"
              type="submit"
              disabled={state.phase === "loading"}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-trust text-white font-semibold px-8 py-4 transition-colors duration-200 hover:bg-trust-dark disabled:opacity-60"
            >
              {state.phase === "loading" ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
              Search receipts
            </button>
          </form>

          {state.phase === "done" && (
            <div className="mt-10">
              {state.results.length === 0 ? (
                <div data-testid="search-empty-state" className="rounded-2xl bg-white border border-black/5 p-10 text-center">
                  <SearchX size={26} className="mx-auto text-gray-300" />
                  <p className="mt-4 font-display font-semibold text-lg">No verified receipts for "{state.searched}" yet.</p>
                  <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
                    That's data too — nobody with proof has reported it. If you've bought from them, your receipt
                    could be the first warning.
                  </p>
                </div>
              ) : (
                <>
                  <p data-testid="search-results-count" className="mb-5 text-sm font-semibold text-gray-500">
                    {state.results.length} verified receipt{state.results.length > 1 ? "s" : ""} for "{state.searched}"
                  </p>
                  <div className="grid sm:grid-cols-2 gap-5">
                    {state.results.map((r, i) => (
                      <ResultCard key={r.id} r={r} i={i} />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
